<script lang="ts">
	import {
		DataHandlerDevice,
		DataHandlerScene,
		type Device,
		Tag,
		type Scene,
		DataHandlerTag,
	} from "luxedo-data"
	import { DragController } from "../../../../stores/DragContext"

	import ProjectorIcon from "../../icons/ProjectorIcon.svelte"
	import { Toast } from "toaster"
	import { RenderProgressIndicator, TileLabel } from "luxedo-comps"
	import { PlusIcon, CheckboxIcon } from "luxedo-icons"
	import { ContextMenuButton, openCtxMenu, triggerContextMenuWithGenerator } from "context-menu"
	import WarningIcon from "../../icons/WarningIcon.svelte"
	import { CTXOptions } from "../../../../modules/ctx-menu/ContextMenuOptions"
	import { ShowLibraryController } from "../../../routes/show-library/ShowLibraryController"
	import { SuperscaleProgressManager } from "../../../../modules/progress-reporting/ProgressContext_Superscale"
	import UnderscaleIndicator from "./scene/UnderscaleIndicator.svelte"
	import { Responsive } from "responsive"
	import { SceneJobFollower } from "../../../../modules/progress-reporting/SceneJobFollower.svelte"

	let device: Device = $state()
	let tags: Array<Tag> = DataHandlerTag.getMany()

	DataHandlerTag.addListener(() => {
		tags = DataHandlerTag.getMany()
	})

	interface Props {
		design: Scene
		isSelected?: boolean
		onClick?: () => void
		lightshowIndex?: number
		remove?: () => void
		showSelectOutline?: boolean
	}

	let {
		design,
		isSelected = false,
		onClick = undefined,
		lightshowIndex = undefined,
		remove = undefined,
		showSelectOutline = false,
	}: Props = $props()

	let triggerRename: () => void = $state()
	let isSelectedInLightshow = $state(false)

	let isUnderscaled = $state(false)
	let isSuperscaling = $state(false)

	ShowLibraryController.LightshowEditor.subscribe((ctx) => {
		isSelectedInLightshow = ctx.selectedScenes.includes(design)
	})

	function getCxtMenuOpts() {
		return CTXOptions.Scene.createOptions(design, { lightshowIndex })
	}

	let isDragging = $state(false)

	function onDragStart() {
		isDragging = true
	}

	function onDragEnd() {
		isDragging = false
	}

	async function onRename(newName: string) {
		try {
			await DataHandlerScene.rename(design, newName)
			Toast.success("Scene renamed.")
		} catch (e) {
			console.error("[ERROR] ", e)

			Toast.error("There was an error renaming this scene, please try again.")
		}
	}

	let unsubscriber
	async function initializeSceneDetails() {
		device = DataHandlerDevice.get(design.target_device_id)
		isUnderscaled = design.isUnderscaled

		if (isUnderscaled) {
			unsubscriber = await SuperscaleProgressManager.subscribeTo(design, (ctx) => {
				isSuperscaling = ctx.hasStarted
				if (ctx.hasFinished) {
					isSuperscaling = false
					isUnderscaled = false
					if (unsubscriber) unsubscriber()
				}
			})
		}
	}

	function onLightshowAddClick(repeat?: boolean) {
		if (!design.render_ver)
			return Toast.error(
				`This scene has not been rendered. To render, open this design within the editor and ${Responsive.isMobile ? "tap" : "click"} the "Render" button.`,
			)

		if (repeat || !isSelectedInLightshow) ShowLibraryController.LightshowEditor.addBlock(design)
		else ShowLibraryController.LightshowEditor.deleteScene(design)
	}

	// What is happening to this scene's render, direct-upload processing or preview, from the live
	// events and the job registry, so a job started before this page loaded or in another tab (the
	// editor) is shown too. Shared with the show overview so both show the same thing.
	const jobs = new SceneJobFollower()

	$effect(() =>
		jobs.follow(design.id, {
			rendered: !!design.render_ver,
			onFail: () => Toast.error(`Processing failed for ${design.name}...`),
			onSuccess: async () => {
				Toast.success(`Processing complete for ${design.name}!`)
				await DataHandlerScene.pull([design.id])
			},
		}),
	)

	$effect(() => {
		if (design) initializeSceneDetails()
	})
</script>

<div class={remove ? "lightshow-block" : ""}>
	<!-- svelte-ignore a11y_click_events_have_key_events -->
	<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
	<div
		class="tile-container {isDragging ? 'is-dragging' : ''} {isSelected
			? 'is-selected'
			: ''} {showSelectOutline ? 'show-lightshow-outline' : ''} {showSelectOutline &&
		isSelectedInLightshow
			? 'is-selected'
			: ''} {showSelectOutline && !design.render_ver ? 'unrendered' : ''} {isUnderscaled
			? 'underscale'
			: ''} {isSuperscaling ? 'supercale' : ''}"
		role="application"
		onclick={showSelectOutline ? () => onLightshowAddClick() : onClick}
		ondragstart={DragController.onDragStart(design, onDragStart, lightshowIndex)}
		ondragend={DragController.onDragEnd(onDragEnd)}
		ontouchstart={DragController.onDragStart(design, onDragStart, lightshowIndex)}
		ontouchcancel={DragController.onDragEnd(onDragEnd)}
		ontouchend={DragController.onDragEnd(onDragEnd)}
		oncontextmenu={(e) => openCtxMenu(e, getCxtMenuOpts())}
		title={design.name}
		draggable={(showSelectOutline || lightshowIndex !== undefined) && !!design.render_ver}
	>
		{#if isSelectedInLightshow && showSelectOutline}
			<button
				onclick={(e) => {
					onLightshowAddClick(true)
					e.stopPropagation()
				}}
				class="lightshow-repeat-scene-button small">Repeat Scene</button
			>
		{/if}
		{#if !showSelectOutline && !remove}
			<div class="ctx-menu-btn">
				<ContextMenuButton onClick={triggerContextMenuWithGenerator(getCxtMenuOpts)} />
			</div>
		{/if}
		<div class="tile-icon-container">
			<div class="tile">
				{#if showSelectOutline}
					<CheckboxIcon isSelected={isSelectedInLightshow} />
				{/if}
				{#if jobs.isProcessing}
					<RenderProgressIndicator progress={jobs.progress} />
				{:else if jobs.isQueued}
					<span class="queued-render" title="Waiting for a processing slot on your account">Queued</span>
				{:else}
					<img src={design.src.thumbnail} alt="Scene Thumbnail" loading="lazy" />
				{/if}
			</div>
			<div class="projector-icon {!device ? 'warn' : ''}">
				{#if device}
					<ProjectorIcon {device} />
				{:else}
					<WarningIcon strokeColor="var(--color-error)" />
				{/if}
			</div>
			{#if isUnderscaled}
				<UnderscaleIndicator scene={design} />
			{/if}
		</div>
		<div class="tile-label-container">
			<TileLabel
				file={design}
				onRename={remove !== undefined ? undefined : onRename}
				bind:triggerRename
			/>
			<!-- {#if design.duration}
				<span class="tile-sublabel"> {design.duration} </span>
			{/if} -->
		</div>
	</div>
	{#if remove}
		<button title="Delete" class="icon" onclick={remove}>
			<PlusIcon />
		</button>
	{/if}
</div>

<style>
	.queued-render {
		line-height: 1;
		text-align: center;
		color: var(--color-text);
		font-size: var(--small);
	}

	.tile {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
	}

	.lightshow-block {
		position: relative;
	}

	.projector-icon {
		width: 1.5rem;
	}

	.projector-icon.warn {
		height: 1.5rem;
	}

	.show-lightshow-outline .tile {
		--tile-border-width: 4px;
		border: var(--tile-border-width) solid var(--light);
		position: relative;
		transition: border-color 250ms;
	}

	.show-lightshow-outline.is-selected .tile {
		border-color: var(--color-main);
	}

	.show-lightshow-outline.unrendered {
		opacity: 0.25;
	}

	.show-lightshow-outline .tile :global(.checkbox-container) {
		position: absolute;
		height: 2rem;
		width: 2rem;
		top: calc(var(--tile-border-width) * -1);
		right: calc(var(--tile-border-width) * -1);
	}

	button.icon {
		padding: 0;
		position: absolute;
		top: 0.5rem;
		right: 0.5rem;
		z-index: 6;
	}

	button.icon :global(svg) {
		margin: 0;
		width: 1rem;
		height: 1rem;
		transform: rotate(45deg);
	}

	button.icon :global(.svg-stroke) {
		stroke: var(--color-text);
	}

	button.icon:hover :global(.svg-stroke),
	button.icon:focus-visible :global(.svg-stroke) {
		stroke: var(--color-red);
	}

	.lightshow-repeat-scene-button {
		z-index: 100;
		position: absolute;
		top: calc(var(--tile-width) - 2rem);
		left: 0;
		right: 0;
		margin-left: auto;
		margin-right: auto;
		transition:
			opacity 250ms,
			background-color 250ms,
			color 250ms,
			transform 250ms;
	}

	:global(.isMobile) .lightshow-repeat-scene-button {
		transform: unset;
	}

	.underscale .tile {
		border-color: var(--ct-red);
	}

	@keyframes superscale {
		0% {
			border-color: var(--color-text);
		}

		25% {
			border-color: var(--color-text);
		}

		50% {
			border-color: var(--color-text-light);
		}

		75% {
			border-color: var(--color-text);
		}

		100% {
			border-color: var(--color-text);
		}
	}

	.supercale .tile {
		animation: superscale 5s infinite;
	}
</style>
